import { useEffect, useMemo, useRef, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";

import { useWhoami } from "../api/queries";
import { ChromeContext, useChrome } from "./Chrome";
import { usePersistedFlag } from "./primitives";

/**
 * Console shell: a nav rail, a search bar, and one scroll region per column. Both left columns
 * fold away — `[` for the nav, `]` for the list — so a load's journal can have the whole width,
 * and the choice is remembered.
 */
export function Layout() {
  const [navCollapsed, setNavCollapsed] = usePersistedFlag("chrome:nav");
  const [listHidden, setListHidden] = usePersistedFlag("chrome:list");

  const chrome = useMemo(
    () => ({ navCollapsed, setNavCollapsed, listHidden, setListHidden }),
    [navCollapsed, setNavCollapsed, listHidden, setListHidden],
  );

  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      const typing =
        event.target instanceof HTMLInputElement || event.target instanceof HTMLSelectElement;
      if (typing || event.metaKey || event.ctrlKey || event.altKey) return;
      if (event.key === "[") setNavCollapsed(!navCollapsed);
      if (event.key === "]") setListHidden(!listHidden);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [navCollapsed, listHidden, setNavCollapsed, setListHidden]);

  return (
    <ChromeContext.Provider value={chrome}>
      <div className="flex h-screen overflow-hidden">
        <Sidebar />
        <div className="flex min-w-0 flex-1 flex-col">
          <header className="flex h-14 shrink-0 items-center gap-4 border-b border-line bg-surface px-5">
            <SearchBox />
            <Operator />
          </header>
          <div className="min-h-0 flex-1 overflow-hidden">
            <Outlet />
          </div>
        </div>
      </div>
    </ChromeContext.Provider>
  );
}

const NAV = [
  { to: "/loads", label: "Loads", hint: "state, timeline, why it didn't sync", icon: "box" },
  { to: "/batches", label: "Batches", hint: "what arrived from devices", icon: "inbox" },
  { to: "/outbound", label: "Outbound", hint: "the CTMS publish queue", icon: "send" },
] as const;

function Sidebar() {
  const { navCollapsed, setNavCollapsed } = useChrome();

  return (
    <aside
      className={`flex shrink-0 flex-col border-r border-line bg-surface transition-[width] ${
        navCollapsed ? "w-14" : "w-56"
      }`}
    >
      <div className="flex h-14 items-center gap-2.5 border-b border-line px-4">
        {/*
          A brand-red accent rule, not a logo: the guidelines say never re-create the mark, and
          the official logomark files live in Notion/Drive.
        */}
        <span className="h-7 w-[3px] shrink-0 rounded-full bg-brand" />
        {!navCollapsed && (
          <NavLink to="/loads" className="flex min-w-0 flex-col leading-tight">
            <span className="truncate text-sm font-semibold tracking-tight text-strong">
              Sync Inspector
            </span>
            <span className="text-[10px] uppercase tracking-wider text-faint">Super Dispatch</span>
          </NavLink>
        )}
        <button
          type="button"
          className="ml-auto text-muted transition-colors hover:text-strong"
          onClick={() => setNavCollapsed(!navCollapsed)}
          title={`${navCollapsed ? "Show" : "Hide"} the nav  ( [ )`}
        >
          <Chevrons pointing={navCollapsed ? "right" : "left"} />
        </button>
      </div>

      <nav className="flex-1 space-y-0.5 overflow-y-auto p-2">
        {NAV.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            title={navCollapsed ? `${item.label} — ${item.hint}` : undefined}
            className={({ isActive }) =>
              `flex items-start gap-2.5 rounded-md py-2 transition-colors ${
                navCollapsed ? "justify-center px-0" : "px-2.5"
              } ${
                isActive ? "bg-elevated text-strong" : "text-body hover:bg-raised hover:text-strong"
              }`
            }
          >
            {({ isActive }) => (
              <>
                <Icon name={item.icon} className={isActive ? "text-link" : "text-faint"} />
                {!navCollapsed && (
                  <span className="min-w-0">
                    <span className="block text-sm font-medium">{item.label}</span>
                    <span className="block truncate text-[11px] text-faint">{item.hint}</span>
                  </span>
                )}
              </>
            )}
          </NavLink>
        ))}
      </nav>
    </aside>
  );
}

const PATHS: Record<string, string> = {
  box: "M3 7l9-4 9 4v10l-9 4-9-4V7zm9-4v18M3 7l9 4 9-4",
  inbox: "M3 13h5l2 3h4l2-3h5M3 13l2.5-8h13L21 13v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6z",
  send: "M3 12l18-8-7 18-3-7-8-3z",
};

export function Chevrons({ pointing }: { pointing: "left" | "right" }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={`size-3.5 shrink-0 ${pointing === "right" ? "rotate-180" : ""}`}
      aria-hidden
    >
      <path d="M11 17l-5-5 5-5M18 17l-5-5 5-5" />
    </svg>
  );
}

function Icon({ name, className = "" }: { name: string; className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={`mt-0.5 size-4 shrink-0 ${className}`}
      aria-hidden
    >
      <path d={PATHS[name]} />
    </svg>
  );
}

/**
 * Whose session this is. Support work gets escalated by pasting a screenshot into a ticket, and
 * "who was looking" is part of the answer — the engine logs the same email against every read.
 */
function Operator() {
  const { data } = useWhoami();
  if (!data) return null;

  return (
    <span className="ml-auto shrink-0 text-xs text-muted" title={data.email}>
      {data.email}
    </span>
  );
}

function SearchBox() {
  const [value, setValue] = useState("");
  const input = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if (event.key === "/" && document.activeElement !== input.current) {
        event.preventDefault();
        input.current?.focus();
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // The box does not resolve anything itself: the search page asks the engine what it is.
  return (
    <form
      className="w-full max-w-xl"
      onSubmit={(event) => {
        event.preventDefault();
        const query = value.trim();
        if (!query) return;
        navigate(`/search?q=${encodeURIComponent(query)}`);
      }}
    >
      <input
        ref={input}
        value={value}
        onChange={(event) => setValue(event.target.value)}
        placeholder="Any guid — load, batch, event, driver, device, vehicle — a load number or a VIN   ( / )"
        spellCheck={false}
        className="field-input mono"
      />
    </form>
  );
}
